import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import Form from '../Form';
import ProfileHeader from './ProfileHeader';
import PersonalInfo from './PersonalInfo';
import UserGroups from './UserGroups';
import PasswordSettings from './PasswordSettings';
import Subscription from './Subscription';
import SubscriptionInfo from './SubscriptionInfo';
import EmailChangeConfirm from './EmailChangeConfirm';
import DeleteUserModal from './DeleteUserModal';
import ConditionalRender from '../ConditionalRender';
import {
  EditProfilePayload,
  Profile as ProfileModel,
  ProfileRenewalStatus,
  ProfileSubscription,
  ProfileSubscriptionStatus,
} from '../../../models/profile.model';
import type { DisplayGroup } from './constants';
import styles from './profile.module.scss';

interface EmailChangeCopy {
  heading?: string;
  warningMessage?: string;
  cancelLabel?: string;
  confirmLabel?: string;
}

interface ProfileProps {
  profile?: ProfileModel;
  isSaving?: boolean;
  onSave: (payload: EditProfilePayload, onDone: () => void) => void;
  onEditClick?: () => void;
  emailChangeCopy?: EmailChangeCopy;

  passwordIsSubmitting?: boolean;
  onSetPassword: (newPassword: string, onSuccess: () => void) => void;
  onSetPasswordClick?: () => void;

  groups: DisplayGroup[];
  canEditGroups?: boolean;
  onEditGroupsClick?: () => void;

  showSubscription?: boolean;
  subscription?: ProfileSubscription;
  renewalStatus?: ProfileRenewalStatus;
  subscriptionStatus?: ProfileSubscriptionStatus;

  showSubscriptionInfo?: boolean;

  canDelete?: boolean;
  isDeleting?: boolean;
  onDeleteConfirm?: () => void;
}

const Profile: React.FC<ProfileProps> = ({
  profile,
  isSaving = false,
  onSave,
  onEditClick,
  emailChangeCopy,
  passwordIsSubmitting,
  onSetPassword,
  onSetPasswordClick,
  groups,
  canEditGroups = false,
  onEditGroupsClick,
  showSubscription = false,
  subscription,
  renewalStatus,
  subscriptionStatus,
  showSubscriptionInfo = false,
  canDelete = false,
  isDeleting = false,
  onDeleteConfirm,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const defaultValues = useMemo(
    () => ({
      name: profile?.name ?? '',
      title: profile?.title ?? '',
      email: profile?.email ?? '',
      phone: profile?.phone ?? '',
    }),
    [profile],
  );

  const exitEdit = () => {
    setConfirmOpen(false);
    setIsEditing(false);
  };

  const toggleEdit = () => {
    if (onEditClick) {
      onEditClick();
      return;
    }
    setIsEditing((prev) => !prev);
  };

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  return (
    <div className={styles.page}>
      <ProfileHeader isEditing={isEditing} onToggleEdit={toggleEdit} profile={profile} />

      <div className={styles.body}>
        <div className={styles.mainColumn}>
          {isEditing ? (
            <div className={styles.editSection}>
              <Form defaultValues={defaultValues} values={defaultValues}>
                {({ formState: { errors, dirtyFields }, getValues }) => {
                  const canSave = Object.keys(errors).length === 0 && Object.keys(dirtyFields).length > 0 && !isSaving;
                  const emailChanged = Boolean(dirtyFields.email);

                  const buildPayload = (): EditProfilePayload => {
                    const values = getValues();
                    const dirtyKeys = Object.keys(dirtyFields) as (keyof EditProfilePayload)[];
                    return dirtyKeys.reduce<EditProfilePayload>((payload, field) => ({ ...payload, [field]: values[field] }), {});
                  };

                  const savePayload = () => {
                    onSave(buildPayload(), exitEdit);
                  };

                  const handleSaveClick = () => {
                    if (!emailChanged) {
                      savePayload();
                    }
                    // when the email changed, the confirm popover opens via its click trigger
                  };

                  const handleConfirmOpenChange = (next: boolean) => {
                    if (next && !emailChanged) return;
                    setConfirmOpen(next);
                  };

                  return (
                    <div className={styles.editForm}>
                      <PersonalInfo isEditing profile={profile} />
                      <EmailChangeConfirm
                        open={confirmOpen}
                        onOpenChange={handleConfirmOpenChange}
                        currentEmail={profile?.email ?? ''}
                        newEmail={getValues('email')}
                        onDiscard={exitEdit}
                        onConfirm={savePayload}
                        heading={emailChangeCopy?.heading}
                        warningMessage={emailChangeCopy?.warningMessage}
                        cancelLabel={emailChangeCopy?.cancelLabel}
                        confirmLabel={emailChangeCopy?.confirmLabel}
                      >
                        <span className={styles.saveButtonWrap}>
                          <Button className={styles.saveButton} disabled={!canSave} loading={isSaving} onClick={handleSaveClick}>
                            Save Changes
                          </Button>
                        </span>
                      </EmailChangeConfirm>
                    </div>
                  );
                }}
              </Form>
            </div>
          ) : (
            <PersonalInfo isEditing={false} profile={profile} />
          )}

          <PasswordSettings
            userName={profile?.name}
            isSubmitting={passwordIsSubmitting}
            onSubmit={onSetPassword}
            onSetPasswordClick={onSetPasswordClick}
          />

          <ConditionalRender visible={showSubscription} hideFallback>
            <Subscription subscription={subscription} renewalStatus={renewalStatus} subscriptionStatus={subscriptionStatus} />
          </ConditionalRender>

          <ConditionalRender visible={showSubscriptionInfo} hideFallback>
            <SubscriptionInfo clientOrgName={profile?.clientOrgName} expiresAt={profile?.subscriptionExpiresAt} />
          </ConditionalRender>
        </div>

        <div className={styles.sideColumn}>
          <UserGroups groups={groups} canEdit={canEditGroups} onEditClick={onEditGroupsClick} />
        </div>
      </div>

      <ConditionalRender visible={canDelete} hideFallback>
        <section className={styles.deleteSection} aria-labelledby="profile-delete-user">
          <h2 className={styles.heading} id="profile-delete-user">
            Delete User
          </h2>
          <p className={styles.deleteText}>Permanently delete this user's account. This action cannot be undone.</p>
          <Button className={styles.deleteButton} danger onClick={openDeleteModal}>
            Delete User
          </Button>
        </section>
      </ConditionalRender>

      <ConditionalRender visible={canDelete} hideFallback>
        <DeleteUserModal
          open={isDeleteModalOpen}
          onClose={closeDeleteModal}
          userName={profile?.name}
          userEmail={profile?.email}
          isDeleting={isDeleting}
          onConfirm={() => onDeleteConfirm?.()}
        />
      </ConditionalRender>
    </div>
  );
};

export default Profile;
